import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { connect } from "react-redux"
import styles from "./WorkOfferGrid.module.scss"
import {
    MapPin,
    Calendar,
    DollarSign,
    Clock,
    Send,
    AlertCircle,
    Building2,
    UserRoundPlus,
    Mail,
    HardHat,
    ChevronLeft,
    ChevronRight,
} from "lucide-react"
import { fireAnalytics } from "../../utils/firebase"
import { useNavigate } from "react-router-dom"
import WorkApplyModal from "../../pages/WorkBag/WorkApplyModal.tsx"
import { userNeedsJobBoardProfileCompletion } from "../../utils/jobboard/jobboardProfileGate.ts"
import { isExternalWorkOffer } from "../../utils/jobboard/isExternalOffer.ts"
import WorkOfferFiltersPublic from "./WorkOfferFiltersPublic.tsx"
import { WorkOffersFilterState, WorkOffersFilterOption } from "../MercadoDeTrabajo/WorkOffersFilters.tsx"
import { getAllWorkOffers } from "../../store/actions"

interface WorkOfferData {
    id: string
    user: {
        username: string
        image_profile: string
        type: string
        razon_social?: string
        name?: string
        last_name?: string
    }
    skill: Array<{
        skill: {
            name: string
        }
    }>
    workZones: Array<{
        jurisdiccion: {
            name: string
        }
        city: string
    }>
    startDate: string
    activeUntil: string
    salary: string
    salaryType: string
    immediateStart: boolean
    description: string
    createdAt: string
}

interface WorkOfferGridProps {
    allJobRequests: any
    getAllWorkOffers: () => void
    scrollToForm: () => void
    userData?: any
}

const WorkOfferCard: React.FC<{ 
    workOffer: WorkOfferData; 
    handleApply: (offer: WorkOfferData, isExternal: boolean) => void 
}> = ({ workOffer, handleApply }) => {
    const [passedStartDate, setPassedStartDate] = useState(false)
    const [formattedSalary, setFormattedSalary] = useState("")
    const [isExternalOffer, setIsExternalOffer] = useState(false)

    useEffect(() => {
        setIsExternalOffer(isExternalWorkOffer(workOffer as any))
    }, [workOffer])


    useEffect(() => {
        const today = new Date()
        if (workOffer?.startDate && new Date(workOffer.startDate) < today) {
            setPassedStartDate(true)
        }
    }, [workOffer])

    useEffect(() => {
        const salary = workOffer?.salary ? Math.floor(Number.parseFloat(workOffer?.salary)) : "0"
        if (workOffer?.salary === "0.00" || salary === "0") {
            setFormattedSalary("A convenir")
        } else if (workOffer?.salaryType === "Hour") {
            setFormattedSalary(`${salary}/h`)
        } else if (workOffer?.salaryType === "Day") {
            setFormattedSalary(`${salary}/día`)
        } else if (workOffer?.salaryType === "Week") {
            setFormattedSalary(`${salary}/sem`)
        } else if (workOffer?.salaryType === "Month") {
            setFormattedSalary(`${salary}/mes`)
        } else if (workOffer?.salaryType === "Job") {
            setFormattedSalary(`${salary}/proy`)
        } else {
            setFormattedSalary(`${salary}`)
        }
    }, [workOffer])

    const calcularDiferenciaTiempo = (fechaCreacion: string) => {
        const fechaActual = new Date()
        const fechaCreacionUTC = new Date(fechaCreacion)

        let diferencia = fechaActual.getTime() - fechaCreacionUTC.getTime()

        if (diferencia < 0) {
            diferencia = 0
        }

        const minutos = Math.floor(diferencia / (1000 * 60))
        const horas = Math.floor(diferencia / (1000 * 60 * 60))
        const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24))

        if (minutos < 1) {
            return "Ahora"
        } else if (minutos < 60) {
            return `${minutos}m`
        } else if (horas < 24) {
            return `${horas}h`
        } else if (dias < 7) {
            return `${dias}d`
        } else {
            return `${Math.floor(dias / 7)}sem`
        }
    }

    const formatStartDate = (date: string) => {
        const day = date?.split("T")[0].split("-")[2]
        const month = date?.split("T")[0].split("-")[1]
        return `${day}/${month}`
    }


    return (
        <div className={`${styles.workOffer_card} ${isExternalOffer ? styles.workOffer_card_external : ""}`}>
            <div className={styles.workOffer_header}>
                <div className={styles.workOffer_companyInfo}>
                    <img
                        src={workOffer?.user?.image_profile || "/placeholder.svg"}
                        alt="Empresa"
                        className={styles.workOffer_companyInfo_avatar}
                    />
                    <div className={styles.workOffer_companyInfo_details}>
                        <h3 className={styles.workOffer_companyInfo_details_name}>
                            {workOffer?.user?.type === "COMPANY"
                                ? workOffer?.user?.razon_social
                                : `${workOffer?.user?.name} ${workOffer?.user?.last_name}`}
                            {isExternalOffer && (
                                <span className={styles.externalBadge}>Búsqueda externa</span>
                            )}
                        </h3>
                        <p className={styles.workOffer_companyInfo_details_time}>
                            {calcularDiferenciaTiempo(workOffer?.createdAt)}
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.workOffer_content}>
                <div className={styles.workOffer_skills}>
                    <div className={styles.workOffer_skills_list}>
                        {workOffer?.skill?.slice(0, 2).map((skill, index) => (
                            <span key={index} className={styles.workOffer_skills_list_item}>
                                {skill?.skill?.name}
                            </span>
                        ))}
                        {workOffer?.skill?.length > 2 && (
                            <span className={styles.workOffer_skills_list_item}>
                                +{workOffer.skill.length - 2}
                            </span>
                        )}
                    </div>
                </div>

                {workOffer?.description && (
                    <div className={styles.workOffer_description}>
                        <p className={styles.workOffer_description_text}>
                            {workOffer.description}
                        </p>
                    </div>
                )}

                <div className={styles.workOffer_details}>
                    <div className={styles.workOffer_details_item}>
                        <MapPin size={14} />
                        <span>{workOffer?.workZones?.[0]?.jurisdiccion?.name}</span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <Calendar size={14} />
                        <span>
                            {workOffer?.immediateStart || passedStartDate
                                ? "Inmediato"
                                : formatStartDate(workOffer?.startDate)}
                        </span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <DollarSign size={14} />
                        <span>{formattedSalary}</span>
                    </div>

                    <div className={styles.workOffer_details_item}>
                        <Clock size={14} />
                        <span>Hasta {formatStartDate(workOffer?.activeUntil)}</span>
                    </div>
                </div>
            </div>

            <div className={styles.workOffer_footer}>
                <button
                    className={styles.workOffer_applyButton}
                    onClick={() => handleApply(workOffer, isExternalOffer)}
                >
                    <Send size={16} />
                    Aplicar
                </button>
            </div>
        </div>
    )
}

const OFFERS_PER_PAGE = 10

function buildPaginationItems(
    currentOneBased: number,
    totalPages: number
): (number | "ellipsis")[] {
    if (totalPages <= 1) return totalPages === 1 ? [1] : []
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pageSet = new Set<number>([
        1,
        totalPages,
        currentOneBased,
        currentOneBased - 1,
        currentOneBased + 1,
    ])
    const sorted = Array.from(pageSet)
        .filter((n) => n >= 1 && n <= totalPages)
        .sort((a, b) => a - b)
    const out: (number | "ellipsis")[] = []
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
            out.push("ellipsis")
        }
        out.push(sorted[i])
    }
    return out
}

const WorkOfferGrid: React.FC<WorkOfferGridProps> = React.memo(({ allJobRequests, getAllWorkOffers, scrollToForm, userData }) => {
    const [showExternalApplyModal, setShowExternalApplyModal] = useState(false)
    const [selectedOffer, setSelectedOffer] = useState<WorkOfferData | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const [filters, setFilters] = useState<WorkOffersFilterState>({
        search: "",
        skills: [],
        zones: [],
    })
    const listTopRef = useRef<HTMLDivElement>(null)
    const skipScrollOnFirstPageEffect = useRef(true)
    const navigate = useNavigate()

    const offers = useMemo(() => allJobRequests?.workOffers ?? [], [allJobRequests?.workOffers])

    const skillOptions = useMemo((): WorkOffersFilterOption[] => {
        const map = new Map<string, number>()
        offers.forEach((offer: WorkOfferData) => {
            (offer.skill ?? []).forEach((item: { skill?: { name?: string } }) => {
                const name = item?.skill?.name
                if (!name) return
                map.set(name, (map.get(name) ?? 0) + 1)
            })
        })
        return Array.from(map.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count }))
    }, [offers])

    const zoneOptions = useMemo((): WorkOffersFilterOption[] => {
        const map = new Map<string, number>()
        offers.forEach((offer: WorkOfferData) => {
            (offer.workZones ?? []).forEach((zone: { jurisdiccion?: { name?: string }; city?: string }) => {
                const label = zone?.jurisdiccion?.name ?? zone?.city
                if (!label) return
                map.set(label, (map.get(label) ?? 0) + 1)
            })
        })
        return Array.from(map.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count }))
    }, [offers])

    const filteredOffers = useMemo(() => {
        const term = filters.search.trim().toLowerCase()
        return offers
            .filter((offer: WorkOfferData) => {
                if (term) {
                    const haystack = [
                        offer.description,
                        offer.user?.razon_social,
                        offer.user?.name,
                        offer.user?.last_name,
                        ...(offer.skill ?? []).map((item: { skill?: { name?: string } }) => item?.skill?.name ?? ""),
                        ...(offer.workZones ?? []).map((zone: { jurisdiccion?: { name?: string }; city?: string }) => zone?.jurisdiccion?.name ?? zone?.city ?? ""),
                    ]
                        .join(" ")
                        .toLowerCase()
                    if (!haystack.includes(term)) return false
                }
                if (filters.skills.length > 0) {
                    const offerSkills = new Set(
                        (offer.skill ?? [])
                            .map((item: { skill?: { name?: string } }) => item?.skill?.name)
                            .filter(Boolean) as string[]
                    )
                    if (!filters.skills.every((skill) => offerSkills.has(skill))) return false
                }
                if (filters.zones.length > 0) {
                    const offerZones = new Set(
                        (offer.workZones ?? [])
                            .map((zone: { jurisdiccion?: { name?: string }; city?: string }) => zone?.jurisdiccion?.name ?? zone?.city)
                            .filter(Boolean) as string[]
                    )
                    if (!filters.zones.some((zone) => offerZones.has(zone))) return false
                }
                return true
            })
            .sort((a: WorkOfferData, b: WorkOfferData) => {
                const dateA = new Date(a.createdAt ?? 0).getTime()
                const dateB = new Date(b.createdAt ?? 0).getTime()
                return dateB - dateA
            })
    }, [offers, filters])

    const total = filteredOffers.length
    const totalPages = Math.ceil(total / OFFERS_PER_PAGE) || 1

    useEffect(() => {
        setCurrentPage(0)
    }, [filters])

    useEffect(() => {
        const maxPage = Math.max(0, totalPages - 1)
        setCurrentPage((p) => Math.min(p, maxPage))
    }, [totalPages])

    useEffect(() => {
        if (skipScrollOnFirstPageEffect.current) {
            skipScrollOnFirstPageEffect.current = false
            return
        }
        listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, [currentPage])

    const pageOffers = useMemo(() => {
        const start = currentPage * OFFERS_PER_PAGE
        return filteredOffers.slice(start, start + OFFERS_PER_PAGE)
    }, [filteredOffers, currentPage])

    // Función para actualizar ofertas manualmente
    const handleRefresh = useCallback(() => {
        setIsLoading(true)
        setHasError(false)
        getAllWorkOffers()
    }, [getAllWorkOffers])

    // Carga inicial forzada si no hay datos
    useEffect(() => {
        if (!allJobRequests && !hasInitiallyLoaded) {
            setIsLoading(true)
            getAllWorkOffers()
        }
    }, [allJobRequests, hasInitiallyLoaded, getAllWorkOffers]) // Solo se ejecuta una vez al montar

    // Auto-refresh cuando el usuario vuelve a la pestaña (opcional)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && hasInitiallyLoaded) {
                // Solo actualizar si han pasado al menos 30 segundos desde la última carga
                const lastUpdate = localStorage.getItem('workOffersLastUpdate')
                const now = Date.now()
                if (!lastUpdate || (now - parseInt(lastUpdate)) > 30000) {
                    handleRefresh()
                    localStorage.setItem('workOffersLastUpdate', now.toString())
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [hasInitiallyLoaded, handleRefresh])

    // Cargar ofertas solo si no están cargadas y no hay error
    useEffect(() => {
        // Verificar si no hay datos cargados (undefined, null, o sin workOffers)
        const hasNoData = !allJobRequests || 
                         !allJobRequests.workOffers || 
                         allJobRequests.workOffers.length === 0
        
        if (hasNoData && !hasError && !isLoading && !hasInitiallyLoaded) {
            setIsLoading(true)
            setHasError(false)
            
            getAllWorkOffers()
        }
    }, [allJobRequests, hasError, isLoading, hasInitiallyLoaded, getAllWorkOffers])

    useEffect(() => {
        const isApiResponse = allJobRequests &&
            typeof allJobRequests === "object" &&
            !Array.isArray(allJobRequests) &&
            ("workOffers" in allJobRequests || "total" in allJobRequests)

        if (isApiResponse) {
            setIsLoading(false)
            if (allJobRequests.error) {
                setHasError(true)
            } else {
                setHasError(false)
                setHasInitiallyLoaded(true)
                try {
                    localStorage.setItem("workOffersLastUpdate", Date.now().toString())
                } catch {
                }
            }
        }
    }, [allJobRequests])

    // Función para reintentar la carga
    const handleRetry = () => {
        setHasError(false)
        setIsLoading(true)
        getAllWorkOffers()
    }

    const handleApply = (offer: WorkOfferData, isExternalOffer: boolean) => {
        fireAnalytics("apply_work_offer", { category: "work_offer" })
        if (isExternalOffer) {
            setShowExternalApplyModal(true)
            setSelectedOffer(offer)
        } else {
            const isEnrolled = Boolean(userData)
            if (!isEnrolled) {
                setSelectedOffer(offer)
                setShowExternalApplyModal(true)
            } else if (userNeedsJobBoardProfileCompletion(userData)) {
                navigate("/bolsadetrabajo/completar-perfil", {
                    state: { fromApply: true, offerId: offer.id },
                })
            } else {
                navigate(`/bolsadetrabajo/${offer.id}`)
            }
        }
    }

    // Estado de carga inicial
    if (isLoading && !hasInitiallyLoaded) {
        return (
            <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <p>Cargando ofertas...</p>
            </div>
        )
    }

    // Estado de error
    if (hasError) {
        return (
            <div className={styles.errorState}>
                <div className={styles.errorState_icon}>
                    <AlertCircle size={48} />
                </div>
                <h2 className={styles.errorState_title}>No pudimos cargar las ofertas</h2>
                <p className={styles.errorState_description}>
                    Ocurrió un problema al cargar las ofertas de trabajo. Por favor intenta nuevamente.
                </p>
                <button className={styles.retryButton} onClick={handleRetry}>
                    <AlertCircle size={18} />
                    Reintentar
                </button>
            </div>
        )
    }

    // Estado vacío (sin ofertas)
    if (allJobRequests?.total === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyState_icon}>
                    <HardHat size={28} />
                </div>
                <h2 className={styles.emptyState_title}>Estamos construyendo las mejores oportunidades</h2>
                <p className={styles.emptyState_description}>
                    Próximamente encontrarás ofertas laborales exclusivas en el sector de la construcción.
                </p>
                <ul className={styles.emptyState_list}>
                    <li className={styles.emptyState_list_item}>
                        <HardHat className={styles.emptyState_list_item_icon} />
                        <div>
                            <strong>Ofertas exclusivas</strong> en construcción
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <UserRoundPlus className={styles.emptyState_list_item_icon} />
                        <div>
                            <strong>Inscribite gratis</strong> y sé el primero
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <Building2 className={styles.emptyState_list_item_icon} />
                        <div>
                            Conectamos con las <strong>mejores empresas</strong>
                        </div>
                    </li>
                    <li className={styles.emptyState_list_item}>
                        <Mail className={styles.emptyState_list_item_icon} />
                        <div>
                            ¿Dudas? <a href="mailto:contacto@edify.la">contacto@edify.la</a>
                        </div>
                    </li>
                </ul>
                <button className={styles.switchButton} onClick={scrollToForm}>
                    <UserRoundPlus size={18} />
                    Inscribirme ahora
                </button>
            </div>
        )
    }

    if (!allJobRequests?.workOffers || allJobRequests.workOffers.length === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyState_icon}>
                    <HardHat size={28} />
                </div>
                <h2 className={styles.emptyState_title}>No hay ofertas disponibles</h2>
                <p className={styles.emptyState_description}>
                    En este momento no hay ofertas de trabajo disponibles. Vuelve a revisar más tarde.
                </p>
                <button className={styles.switchButton} onClick={scrollToForm}>
                    <UserRoundPlus size={18} />
                    Inscribirme ahora
                </button>
            </div>
        )
    }

    if (filteredOffers.length === 0) {
        return (
            <div className={styles.workOfferGrid}>
                <div className={styles.workOfferGridColumns}>
                <div className={styles.filtersWrapper}>
                    <WorkOfferFiltersPublic
                        skills={skillOptions}
                        zones={zoneOptions}
                        value={filters}
                        onChange={setFilters}
                        totalCount={0}
                        sidebar
                    />
                </div>
                <div className={styles.gridMain}>
                <div className={styles.emptyState}>
                    <div className={styles.emptyState_icon}>
                        <HardHat size={28} />
                    </div>
                    <h2 className={styles.emptyState_title}>No encontramos ofertas con esos criterios</h2>
                    <p className={styles.emptyState_description}>
                        Probá ajustando los filtros o explorá todas las oportunidades disponibles.
                    </p>
                    <button className={styles.switchButton} onClick={() => setFilters({ search: "", skills: [], zones: [] })}>
                        Limpiar filtros
                    </button>
                </div>
                </div>
                </div>
            </div>
        )
    }

    const safePage = Math.min(currentPage, Math.max(0, totalPages - 1))
    const rangeStart = total === 0 ? 0 : safePage * OFFERS_PER_PAGE + 1
    const rangeEnd = total === 0 ? 0 : Math.min(safePage * OFFERS_PER_PAGE + pageOffers.length, total)

    const canPrev = safePage > 0
    const canNext = safePage < totalPages - 1

    const goPrev = () => {
        setCurrentPage((p) => Math.max(0, p - 1))
    }

    const goNext = () => {
        setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
    }

    const paginationItems = totalPages > 1 ? buildPaginationItems(safePage + 1, totalPages) : []

    return (
        <div className={styles.workOfferGrid}>
            <div className={styles.workOfferGridColumns}>
            <aside className={styles.filtersWrapper}>
                <WorkOfferFiltersPublic
                    skills={skillOptions}
                    zones={zoneOptions}
                    value={filters}
                    onChange={setFilters}
                    totalCount={total}
                    sidebar
                />
                {total > 0 && (
                    <div className={styles.sidebarListMeta}>
                        <p className={styles.sidebarListMetaTitle}>
                            Página {safePage + 1} de {totalPages}
                        </p>
                        <p className={styles.sidebarListMetaRange}>
                            Mostrando {rangeStart}–{rangeEnd} de {total} ofertas
                        </p>
                    </div>
                )}
            </aside>
            <div ref={listTopRef} className={styles.gridMain}>
            <div className={styles.offersGrid} role="list">
                {pageOffers.map((workOffer: WorkOfferData) => (
                    <div key={workOffer.id} className={styles.offersGridCell} role="listitem">
                        <WorkOfferCard
                            workOffer={workOffer}
                            handleApply={handleApply}
                        />
                    </div>
                ))}
            </div>
            </div>
            </div>

            {totalPages > 1 && (
                <nav className={styles.bottomPagination} aria-label="Paginación de ofertas">
                    <div className={styles.bottomPaginationInner}>
                        <button
                            type="button"
                            className={styles.bottomPaginationNav}
                            onClick={goPrev}
                            disabled={!canPrev}
                            aria-label="Página anterior"
                        >
                            <ChevronLeft size={22} />
                            <span>Anterior</span>
                        </button>
                        <div className={styles.bottomPaginationPages} role="group" aria-label="Números de página">
                            {paginationItems.map((item, idx) =>
                                item === "ellipsis" ? (
                                    <span
                                        key={`ellipsis-${idx}`}
                                        className={styles.bottomPaginationEllipsis}
                                        aria-hidden
                                    >
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={item}
                                        type="button"
                                        className={styles.bottomPaginationPage}
                                        data-active={item === safePage + 1}
                                        onClick={() => setCurrentPage(item - 1)}
                                        aria-label={`Ir a página ${item}`}
                                        aria-current={item === safePage + 1 ? "page" : undefined}
                                    >
                                        {item}
                                    </button>
                                )
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.bottomPaginationNav}
                            onClick={goNext}
                            disabled={!canNext}
                            aria-label="Página siguiente"
                        >
                            <span>Siguiente</span>
                            <ChevronRight size={22} />
                        </button>
                    </div>
                </nav>
            )}

            {showExternalApplyModal && (
                <WorkApplyModal
                    workOffer={selectedOffer}
                    setShowApplyModal={setShowExternalApplyModal}
                    setShowForm={scrollToForm}
                    forbiddenClose={false}
                />
            )}
        </div>
    )
})

const mapStateToProps = (state: any) => {
    return {
        allJobRequests: state.register.allJobRequests,
        userData: state.general?.userData,
    }
}

export default connect(mapStateToProps, { getAllWorkOffers })(WorkOfferGrid)
